import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Loader2, ShieldCheck, Check } from "lucide-react";
import { submitActivation } from "../lib/api";
import { toast } from "sonner";

export default function Attivazione() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    azienda: "",
    email: "",
    sito: searchParams.get("sito") || "",
    partita_iva: "",
    note: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefono.trim() || !form.email.trim() || !form.azienda.trim()) {
      toast.error("Compila almeno nome, azienda, telefono e email");
      return;
    }
    setLoading(true);
    try {
      await submitActivation({
        ...form,
        session_id: searchParams.get("session_id") || null,
      });
      navigate("/grazie");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore, riprova tra poco.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="attivazione-page">
      <header className="border-b border-brand-line bg-white/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-brand-inkSoft hover:text-brand-ink transition" data-testid="attivazione-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Indietro</span>
          </Link>
          <span className="font-heading text-lg text-brand-ink">24Vox</span>
          <div className="w-16"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <p className="text-xs font-medium tracking-[0.25em] text-brand-terra uppercase mb-4">Attivazione</p>
        <h1 className="font-heading text-4xl md:text-5xl text-brand-ink mb-4 leading-tight">
          Hai deciso. Bene.
        </h1>
        <p className="text-lg text-brand-inkSoft mb-10 max-w-xl">
          Lasciami i tuoi dati e ti richiamo io entro 24 ore per attivare la <strong className="text-brand-ink">tua 24Vox</strong>: 588€/anno per la piattaforma, chiamate a consumo al costo. Senza commerciali di mezzo.
        </p>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {[
            { icon: Check, title: "Risposta in 24h", text: "Ti chiamo io, robS" },
            { icon: ShieldCheck, title: "Garanzia 30 giorni", text: "Se non funziona, rimborsiamo" },
            { icon: Check, title: "Setup 5 minuti", text: "Già operativa il giorno stesso" },
          ].map((b, i) => (
            <div key={i} className="bg-white border border-brand-line rounded-xl p-4 flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-brand-olive/10 flex items-center justify-center shrink-0">
                <b.icon className="w-4 h-4 text-brand-olive" />
              </div>
              <div>
                <p className="font-heading text-sm text-brand-ink">{b.title}</p>
                <p className="text-xs text-brand-inkSoft">{b.text}</p>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-brand-line rounded-2xl p-6 md:p-8 space-y-5" data-testid="attivazione-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">Nome e cognome *</label>
              <input name="nome" value={form.nome} onChange={handleChange} required className="input-clean w-full px-4 py-3 rounded-lg" data-testid="att-nome" />
            </div>
            <div>
              <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">Telefono *</label>
              <input name="telefono" value={form.telefono} onChange={handleChange} required placeholder="333 1234567" className="input-clean w-full px-4 py-3 rounded-lg" data-testid="att-telefono" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">Nome azienda *</label>
              <input name="azienda" value={form.azienda} onChange={handleChange} required className="input-clean w-full px-4 py-3 rounded-lg" data-testid="att-azienda" />
            </div>
            <div>
              <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">Email aziendale *</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} required className="input-clean w-full px-4 py-3 rounded-lg" data-testid="att-email" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">Sito web</label>
              <input name="sito" value={form.sito} onChange={handleChange} placeholder="tenutaciminata.it" className="input-clean w-full px-4 py-3 rounded-lg" data-testid="att-sito" />
            </div>
            <div>
              <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">Partita IVA</label>
              <input name="partita_iva" value={form.partita_iva} onChange={handleChange} className="input-clean w-full px-4 py-3 rounded-lg" data-testid="att-piva" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">Note (opzionale)</label>
            <textarea name="note" value={form.note} onChange={handleChange} rows="3" placeholder="Quando ti chiamo? Qualcosa di specifico che dovrei sapere?" className="input-clean w-full px-4 py-3 rounded-lg resize-none" data-testid="att-note"></textarea>
          </div>

          <button type="submit" disabled={loading} className="btn-olive w-full px-6 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="att-submit">
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Invio in corso...</>
            ) : (
              <><Send className="w-5 h-5" />Richiama-mi entro 24 ore</>
            )}
          </button>
          <p className="text-xs text-brand-inkSoft text-center">
            Niente pagamento ora. Ne parliamo al telefono, poi se ti convince paghi.
          </p>
        </form>
      </main>
    </div>
  );
}
